// src/pages/Reports/ReserveSurplusBalance/ReserveSurplusBalanceReport.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import {
  Card,
  Row,
  Col,
  Button,
  Typography,
  DatePicker,
  Select,
  AutoComplete,
  Input,
  Table,
  message,
  Grid,
  Flex,
} from "antd";
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  PrinterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import commaNumber from "comma-number";
import _ from "lodash";

import ReportDom from "../../../components/common/ReportDom";
import {
  currentDateStartTime,
  currentDateEndTime,
} from "../../../lib/functions";
import { getReserveSurplusBalanceApi } from "../../../apiservice/reports";
import { getAccountBySeachApi } from "../../../apiservice/setting";
import { useLocation, useSearchParams } from "react-router-dom";
import CommonDateRangeFilter from "../../../components/common/filters/CommonDateRangeFilter";
import { exportSuspenseBalanceCsv } from "../SuspenseBalance/exportSuspenseBalanceCsv";
import "../../../components/common/bt.css";

const { Title } = Typography;
const format = commaNumber.bindWith(",", ".");

const ReserveSurplusBalanceReport = () => {
  const navigate = useNavigate();
  const reportRef = useRef();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    filterType: "all", // all | account | date
    account: null,
    fromDate: currentDateStartTime(dayjs()),
    toDate: currentDateEndTime(dayjs()),
  });

  const [data, setData] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [balanceTotal, setBalanceTotal] = useState(0);
  const [accLoading, setAccLoading] = useState(false);
  const [accountQuery, setAccountQuery] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  // handleFilterChange only queues the update, so running the report right here
  // would still use the old (default) filters — flag it and run it from the
  // next effect, once the drill-down filters have been applied.
  const [drillReady, setDrillReady] = useState(false);
  useEffect(() => {
    const path = pathname.split("/")[3];
    if (path) {
      handleFilterChange("filterType", "date");
      const fromDate = searchParams.get("fromDate");
      if (fromDate) {
        handleFilterChange("fromDate", fromDate);
      }
      handleFilterChange("toDate", path);
      setDrillReady(true);
    }
  }, []);
  useEffect(() => {
    if (drillReady) fetchReport();
  }, [drillReady]);

  // fetch accounts whenever query changes
  useEffect(() => {
    if (filters.filterType !== "account") return;
    fetchAccounts(accountQuery);
  }, [accountQuery]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const fetchReport = async () => {
    if (filters.filterType === "account" && !filters.account) {
      message.warning("Please select an account");
      return;
    }
    setLoading(true);
    try {
      let payload = {};

    if (filters.filterType === "all") {
      payload = { accId: null }; // ✅ all accounts
    } else if (filters.filterType === "account") {
      payload = { accId: filters.account?.acc_id ?? null }; // ✅ specific account
    } else if (filters.filterType === "date") {
      payload = {
        fromDate: filters.fromDate,
        toDate: filters.toDate, // ✅ only dates, no accId
      };
    }

 
      const res = await getReserveSurplusBalanceApi(payload);

      const mapped = (res.data.reserveSurplus || []).map((item) => ({
        ...item,
        opening_balance: item.opening_balance ?? item.curr_opening_balance ?? 0,
        received_total: item.received_total ?? item.debit_amount_total ?? 0,
        pay_total: item.pay_total ?? item.credit_amount_total ?? 0,
        balance: item.balance ?? 0,
      }));

      setData(mapped);
      setBalanceTotal(res.data.total_balance || 0);
    } catch (err) {
      console.error(err);
      message.error("Error fetching Reserve Surplus Balance Report");
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async (query) => {
    setAccLoading(true);
    try {
      const res = await getAccountBySeachApi({
        query: query?.trim() || "",
        multiType: ["reserve_surplus"],
      });
      setAccounts(res.data || []);
    } catch (err) {
      console.error("Error fetching accounts:", err);
    } finally {
      setAccLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "Reserve Surplus Balance Report",
    removeAfterPrint: true,
  });

  const handleNavigateToBack = () => {
    navigate("/reports");
  };
  const handleDownloadCsv = () => {                        // 👈 added
    exportSuspenseBalanceCsv({
      data,
      balanceTotal,
      title:    "Suspense Balance Report",
      filename: "suspense_balance",
    });
  };
  const columns = [
    { title: "No.", render: (_, __, i) => i + 1 },
    { title: "Account Name", dataIndex: "acc_name" },
    {
      title: "Opening Balance",
      dataIndex: "opening_balance",
      align: "right",
      render: (v) => format(Number(v || 0).toFixed(2)),
    },
    {
      title: "Debit Amount",
      dataIndex: "received_total",
      align: "right",
      render: (v) => format(Number(v || 0).toFixed(2)),
    },
    {
      title: "Credit Amount",
      dataIndex: "pay_total",
      align: "right",
      render: (v) => format(Number(v || 0).toFixed(2)),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      align: "right",
      render: (v) => format(Number(v || 0).toFixed(2)),
    },
  ];

  return (
    <div className="p-4">
      {/* Header */}
      <Card
        className="shadow-md rounded-xl mb-4"
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleNavigateToBack}
              style={{ marginRight: 8 }}
            />
            <Title level={4} style={{ margin: 0 }} className="reportBigTitle" >
              Reserve Surplus Balance Report
            </Title>
          </div>
        }
         extra={
          data.length > 0 ? (                              // 👈 guard added + Flex wrapper
            <Flex gap={8}>
              <Button icon={<DownloadOutlined />} onClick={handleDownloadCsv}>
                {!screens.xs && "CSV"}
              </Button>
              <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
                {!screens.xs && "Print"}
              </Button>
            </Flex>
          ) : (
            <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
              {!screens.xs && "Print"}
            </Button>
          )
        }
      />

      {/* Filters */}
      <Card className="shadow-md rounded-xl mb-4">
        <Row gutter={[16, 16]}>
          {/* Filter Type */}
          <Col xs={24} sm={12} md={6} lg={6}>
            <label>Filter Type</label>
            <Select
              value={filters.filterType}
              onChange={(val) => handleFilterChange("filterType", val)}
              style={{ width: "100%" }}
              options={[
                { value: "all", label: "All Reserve Surplus Accounts" },
                { value: "account", label: "Account Wise" },
                { value: "date", label: "Date Wise" },
              ]}
            />
          </Col>

          {/* Account Wise */}
          {filters.filterType === "account" && (
            <Col xs={24} sm={12} md={6} lg={6}>
              <label>Select Account</label>
              <AutoComplete
                style={{ width: "100%" }}
                options={accounts.map((a) => ({
                  label: `${a.acc_name} - ${a.acc_code || ""}`,
                  value: `${a.acc_name} - ${a.acc_code || ""}`,
                  account: a,
                }))}
                onSelect={(_, option) => {
                  handleFilterChange("account", option.account);
                }}
                onSearch={fetchAccounts}
                placeholder="Search Account"
                loading={accLoading}
                allowClear
              >
                <Input />
              </AutoComplete>
            </Col>
          )}

          {/* Date Wise */}
          {filters.filterType === "date" && (
            <>
            <CommonDateRangeFilter
              filters={filters}
              onChange={(fromDate, toDate) => {
              handleFilterChange("fromDate", fromDate);
              handleFilterChange("toDate", toDate);
              }}
            />
              {/* <Col xs={24} sm={12} md={4} lg={4}>
                <label>From Date</label>
                <DatePicker
                  value={dayjs(filters.fromDate)}
                  onChange={(d) =>
                    handleFilterChange(
                      "fromDate",
                      currentDateStartTime(dayjs(d))
                    )
                  }
                  style={{ width: "100%" }}
                />
              </Col>
              <Col xs={24} sm={12} md={4} lg={4}>
                <label>To Date</label>
                <DatePicker
                  value={dayjs(filters.toDate)}
                  onChange={(d) =>
                    handleFilterChange("toDate", currentDateEndTime(dayjs(d)))
                  }
                  style={{ width: "100%" }}
                />
              </Col> */}
            </>
          )}

          {/* Report Button */}
          <Col
            xs={24}
            sm={12}
            md={4}
            lg={4}
            style={{ display: "flex", alignItems: "flex-end" }}
          >
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={fetchReport}
              loading={loading}
              block
            >
              Report
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Report Preview */}
      <Card className="shadow-md rounded-xl">
        <Table
          dataSource={data}
          rowKey={(_, i) => i}
          columns={columns}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
          }}
          onChange={handleTableChange}
          bordered
          size="small"
          scroll={{ x: "max-content" }}
          summary={() =>
            data.length > 0 && (
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={2} align="right">
                  Grand Total: 
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {format(
                    _.sumBy(data, (r) =>
                      Number(r.opening_balance || 0)
                    ).toFixed(2)
                  )}
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {format(
                    _.sumBy(data, (r) => Number(r.received_total || 0)).toFixed(
                      2
                    )
                  )}
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {format(
                    _.sumBy(data, (r) => Number(r.pay_total || 0)).toFixed(2)
                  )}
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {format(balanceTotal.toFixed(2))}
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )
          }
        />
      </Card>

      {/* Print Layout */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="Reserve Surplus Balance Report"
          mode="report-page"
          filters={filters}
          renderTable={() => (
            <table className="ledger-print-table">
              <colgroup>
                <col style={{ width: "6%" }} />
                <col style={{ width: "28%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "18%" }} />
              </colgroup>
              <tbody>
                <tr className="ledger-print-heading">
                  <td>No.</td>
                  <td>Account Name</td>
                  <td style={{ textAlign: "right" }}>Opening Balance</td>
                  <td style={{ textAlign: "right" }}>Debit Amount</td>
                  <td style={{ textAlign: "right" }}>Credit Amount</td>
                  <td style={{ textAlign: "right" }}>Balance</td>
                </tr>
                {data.map((row, index) => (
                  <tr key={row.acc_id || index}>
                    <td>{index + 1}</td>
                    <td>{row.acc_name}</td>
                    <td style={{ textAlign: "right" }}>
                      {format(Number(row.opening_balance || 0).toFixed(2))}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {format(Number(row.received_total || 0).toFixed(2))}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {format(Number(row.pay_total || 0).toFixed(2))}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {format(Number(row.balance || 0).toFixed(2))}
                    </td>
                  </tr>
                ))}
                <tr className="ledger-total-row">
                  <td colSpan={2} style={{ textAlign: "right" }}>
                    Grand Total
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {format(
                      _.sumBy(data, (r) => Number(r.opening_balance || 0)).toFixed(2)
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {format(
                      _.sumBy(data, (r) => Number(r.received_total || 0)).toFixed(2)
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {format(
                      _.sumBy(data, (r) => Number(r.pay_total || 0)).toFixed(2)
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {format(Number(balanceTotal || 0).toFixed(2))}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        />
      </div>
    </div>
  );
};

export default ReserveSurplusBalanceReport;
